import { useMemo, useState } from "react";
import CarrierBadge from "../../components/CarrierBadge";
import Icon from "../../components/Icon";
import StepHeader from "../../components/StepHeader";
import { carrierLogoSrc, presentableCarrierText } from "../../lib/carrier-brand";
import { isLetterOnlyShipment } from "@shared/rate-filtering";
import type { RateQuote, RulesEvaluationResult, SelectedContent, ShipmentPreferences } from "@shared/types";

interface Props {
  quotes: RateQuote[];
  rulesEvaluation: RulesEvaluationResult | null;
  unavailableProviders: string[];
  preferences: ShipmentPreferences | null;
  contents: SelectedContent[];
  selecting: string | null;
  onSelect: (quote: RateQuote) => void;
  onBack: () => void;
}

const TAG_LABELS: Record<string, string> = {
  BEST_VALUE: "Best value",
  CHEAPEST: "Cheapest",
  FASTEST: "Fastest",
  NO_PRINTER_NEEDED: "No printer needed",
  NEAREST_DROPOFF: "Nearest drop-off",
  MOST_TRACKED: "Fully tracked",
};

// Step 7 — compare & choose. Filters by printer capability if the sender
// said they have none, sorts by stated priority, and — critically for
// "tricky items" — surfaces the rules engine's plain-English guidance when
// nothing is currently compatible, rather than just showing an empty list.
const INITIAL_VISIBLE_COUNT = 4;

export default function RatesStep({ quotes, rulesEvaluation, unavailableProviders, preferences, contents, selecting, onSelect, onBack }: Props) {
  const [showAll, setShowAll] = useState(false);
  const letterOnly = isLetterOnlyShipment(contents);

  const sortedQuotes = useMemo(() => {
    let list = [...quotes];
    if (preferences?.printerCapability === "NO") {
      list = list.filter((q) => q.printerlessAvailable);
    }
    if (preferences?.priority === "COLLECTION_FROM_HOME") {
      list = list.filter((q) => q.fulfilment === "COLLECTION" || q.fulfilment === "BOTH");
    } else if (preferences?.priority === "NEAREST_DROPOFF") {
      list = list.filter((q) => q.fulfilment === "DROPOFF" || q.fulfilment === "BOTH");
    }
    switch (preferences?.priority) {
      case "CHEAPEST":
        list.sort((a, b) => a.priceGbp - b.priceGbp);
        break;
      case "FASTEST":
        list.sort((a, b) => parseInt(a.estimatedDeliveryDays) - parseInt(b.estimatedDeliveryDays));
        break;
      case "TRACKED":
        list.sort((a, b) => trackRank(b.trackedLevel) - trackRank(a.trackedLevel));
        break;
      case "NEAREST_DROPOFF":
        list.sort((a, b) => (a.nearestDropoffDistanceMiles ?? 999) - (b.nearestDropoffDistanceMiles ?? 999));
        break;
      default:
        list.sort((a, b) => a.priceGbp - b.priceGbp);
    }
    return list;
  }, [quotes, preferences]);

  // The initial preview picks each carrier's best-sorted option, capped at
  // INITIAL_VISIBLE_COUNT — otherwise a carrier with several near-identical
  // cheap services (e.g. Evri's ParcelShop variants) can fill the whole
  // preview on its own. "Show more" reveals the untouched full sorted list,
  // same-carrier duplicates included, for a real side-by-side comparison.
  const topByCarrier = useMemo(() => {
    const seen = new Set<string>();
    const picks: RateQuote[] = [];
    for (const q of sortedQuotes) {
      if (seen.has(q.carrier)) continue;
      seen.add(q.carrier);
      picks.push(q);
      if (picks.length >= INITIAL_VISIBLE_COUNT) break;
    }
    return picks;
  }, [sortedQuotes]);

  const visibleQuotes = showAll ? sortedQuotes : topByCarrier;
  const hiddenCount = sortedQuotes.length - visibleQuotes.length;

  return (
    <div className="stack fade-in-up">
      <StepHeader title="Compare options" onBack={onBack} />
      <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
        {letterOnly
          ? "We've limited this to services designed for letter-sized post."
          : preferences?.priority === "COLLECTION_FROM_HOME"
            ? "Only services that collect from your address are shown."
            : "We've already filtered out anything that can't accept your parcel."}
      </p>

      {letterOnly && (
        <div className="banner banner-info">
          <span>Standard stamps can cost less. This list only includes services the app can book and produce a label for.</span>
        </div>
      )}

      {unavailableProviders.length > 0 && (
        <div className="banner banner-info">
          <span>Some carriers are temporarily unavailable — other options are still shown below.</span>
        </div>
      )}

      {rulesEvaluation && rulesEvaluation.guidance.length > 0 && (
        <div className="banner banner-warn">
          <div className="stack-sm">
            {rulesEvaluation.guidance.map((g, i) => (
              <p key={i} style={{ margin: 0 }}>
                {g}
              </p>
            ))}
          </div>
        </div>
      )}

      {rulesEvaluation?.overallOutcome === "MANUAL_CHECK_REQUIRED" && (
        <div className="banner banner-warn">
          <span>Something you're sending needs a manual check before we can confirm it's postable. Please contact Michael directly before sending.</span>
        </div>
      )}

      {sortedQuotes.length === 0 && rulesEvaluation?.overallOutcome !== "NO_COMPATIBLE_CARRIER" && (
        <div className="banner banner-info">
          <span>
            {preferences?.priority === "COLLECTION_FROM_HOME"
              ? "No compatible home-collection service is available right now. Go back and choose another preference."
              : preferences?.priority === "NEAREST_DROPOFF"
                ? "No compatible drop-off service is available right now. Go back and choose another preference."
                : "No shipping options are available right now — please check back shortly."}
          </span>
        </div>
      )}

      <div className="stack stagger-in">
        {visibleQuotes.map((q) => {
          const key = `${q.providerId}:${q.carrier}:${q.service}`;
          const isSelecting = selecting === key;
          return (
          <button
            key={`${q.providerId}-${q.carrier}-${q.service}`}
            type="button"
            className={`quote-card${isSelecting ? " quote-card-selecting" : ""}`}
            aria-pressed={isSelecting}
            aria-busy={isSelecting}
            onClick={() => onSelect(q)}
            disabled={selecting !== null}
          >
            <div className="quote-header">
              <div className="quote-title">
                <CarrierBadge carrier={q.carrier} displayName={presentableCarrierText(q.carrier, q.carrierDisplayName)} logoSrc={carrierLogoSrc(q.carrier)} size={36} />
                <div>
                  <strong>{presentableCarrierText(q.carrier, q.carrierDisplayName)}</strong>
                  <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                    {presentableCarrierText(q.carrier, q.serviceDisplayName)}
                  </div>
                </div>
              </div>
              <span className="quote-price">£{q.priceGbp.toFixed(2)}</span>
            </div>

            {(q.recommendationTags.some((t) => t !== "NO_PRINTER_NEEDED") || q.isMock) && (
              <div className="tag-row">
                {q.recommendationTags
                  .filter((t) => t !== "NO_PRINTER_NEEDED")
                  .map((t) => (
                    <span className="tag" key={t}>
                      {TAG_LABELS[t] ?? t}
                    </span>
                  ))}
                {q.isMock && (
                  <span className="tag" style={{ background: "var(--color-warn-bg)", color: "var(--color-warn-text)" }}>
                    Test data
                  </span>
                )}
              </div>
            )}

            <div className="quote-meta">
              <span className="quote-meta-item">
                <Icon name="clock" size={14} strokeWidth={2} />
                {q.estimatedDeliveryDays}
              </span>
              <span className="quote-meta-item">
                <Icon name="shield-check" size={14} strokeWidth={2} />
                {trackLabel(q.trackedLevel)}
              </span>
              <span className="quote-meta-item">
                <Icon name={q.printerlessAvailable ? "printer-off" : "printer"} size={14} strokeWidth={2} />
                {q.printerlessAvailable ? "No printer needed" : "Printer required"}
              </span>
              {(q.fulfilment === "COLLECTION" || q.fulfilment === "BOTH") && (
                <span className="quote-meta-item">
                  <Icon name="home" size={14} strokeWidth={2} />
                  {q.fulfilment === "BOTH" ? "Drop off or home collection" : "Collected from home"}
                </span>
              )}
              {q.fulfilment !== "COLLECTION" && q.nearestDropoffDistanceMiles !== null && (
                <span className="quote-meta-item">
                  <Icon name="map-pin" size={14} strokeWidth={2} />
                  {q.nearestDropoffDistanceMiles} mi
                </span>
              )}
              {q.compensationCoverGbp !== null && q.compensationCoverGbp > 0 && (
                <span className="quote-meta-item">
                  <Icon name="coin" size={14} strokeWidth={2} />
                  Cover to £{q.compensationCoverGbp}
                </span>
              )}
            </div>

            {q.warnings.length > 0 && (
              <div className="stack-sm" style={{ marginTop: "var(--space-1)" }}>
                {q.warnings.map((w, i) => (
                  <div key={i} className="banner banner-warn" style={{ padding: "var(--space-2) var(--space-3)" }}>
                    <span style={{ fontSize: "0.85rem" }}>{w}</span>
                  </div>
                ))}
              </div>
            )}
            {isSelecting && (
              <span className="quote-selecting-status" role="status">
                <span className="spinner spinner-inline" aria-hidden="true" />
                Checking this service…
              </span>
            )}
          </button>
          );
        })}
      </div>

      {hiddenCount > 0 && (
        <button type="button" className="btn btn-secondary" onClick={() => setShowAll(true)}>
          Show {hiddenCount} more option{hiddenCount === 1 ? "" : "s"}
        </button>
      )}
    </div>
  );
}

function trackRank(level: RateQuote["trackedLevel"]): number {
  return level === "FULL" ? 2 : level === "BASIC" ? 1 : 0;
}

function trackLabel(level: RateQuote["trackedLevel"]): string {
  return level === "FULL" ? "Fully tracked" : level === "BASIC" ? "Basic tracking" : "No tracking";
}
